import type { ReactNode } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { House } from 'lucide-react'
import { useAuth, myRoleLabels } from '../core/auth'
import { MODULES } from '../core/modules'
import { ROLE_TOOLS, TOOL_LABELS } from '../core/caps'
import { previewLabel } from '../core/preview'
import { Eye } from 'lucide-react'
import { AccountMenu } from './AccountMenu'
import { Bell } from './Bell'

/**
 * The desktop rail. Same source as the home tiles — a person sees exactly the
 * tools their grants allow, in both places, because both read MODULES.
 * Hidden below 900px, where the home screen is the navigation.
 */
function SideNav() {
  const { canUseTool, profile, student } = useAuth()
  const { pathname } = useLocation()
  if (!profile && !student) return null
  const mine = MODULES.filter((m) => m.enabled && canUseTool(m.id))
  if (mine.length === 0) return null
  return (
    <nav className="rail" aria-label="Tools">
      <NavLink to="/" end className={({ isActive }) => 'rail__link' + (isActive ? ' is-active' : '')}>
        <House size={17} strokeWidth={1.75} aria-hidden="true" /> Home
      </NavLink>
      {mine.map((m) => (
        <div key={m.id}>
          <NavLink to={m.path}
            className={({ isActive }) => 'rail__link' + (isActive ? ' is-active' : '')}>
            <m.icon size={17} strokeWidth={1.75} aria-hidden="true" /> {m.title}
            {m.Badge && <span style={{ marginLeft: 'auto' }}><m.Badge /></span>}
          </NavLink>
          {/* A tool's steps and pages open under it while you are in it (or
              everywhere, when it is your only tool), so a second tool's link
              is never pushed off the bottom by the first one's list. */}
          {m.NavChildren && (mine.length === 1 || pathname === m.path || pathname.startsWith(m.path + '/')) && <m.NavChildren />}
        </div>
      ))}
    </nav>
  )
}

/**
 * Who you are, in the tool you are looking at. Worked out from what you hold
 * rather than stored, so it can never disagree with your real access.
 *
 * Inside a tool it names that tool; anywhere else it names every tool you have
 * a role in. Links to the roles page, which says what each role is for.
 */
function RoleBadge() {
  const a = useAuth()
  const { profile, student } = a
  const { pathname } = useLocation()
  if (!profile && !student) return null

  const here = MODULES.find((m) => pathname === m.path || pathname.startsWith(m.path + '/'))
  const tools = here ? [here.id as string] : ROLE_TOOLS.filter((t) => t !== 'admin')
  const parts = profile?.districtAdmin
    ? ['District admin']
    : tools
        .map((t) => ({ t, labels: myRoleLabels(a, t) }))
        .filter((x) => x.labels.length)
        .map((x) => `${TOOL_LABELS[x.t] ?? x.t} · ${x.labels.join(', ')}`)
  if (!parts.length) return null

  // Students get the label but not the link — the roles page is about staff jobs.
  if (student) return <span className="shell__role">{parts.join('  |  ')}</span>

  return (
    <Link to="/roles" className="shell__role" title="What this role can do">
      <span className="visually-hidden">Your role: </span>
      {parts.join('  |  ')}
    </Link>
  )
}

/**
 * Always on screen while previewing — in a room full of staff, nobody should
 * wonder whether what they are watching is real or whether a save worked.
 */
function PreviewBanner() {
  const { preview, setPreview } = useAuth()
  if (!preview) return null
  const label = previewLabel(preview)
  return (
    <div className="preview-bar" role="status">
      <Eye size={16} aria-hidden="true" />
      <span><strong>Previewing {label}</strong> · this is what they see. Nothing you do here is saved.</span>
      <button className="preview-bar__stop" onClick={() => setPreview(null)}>Stop preview</button>
    </div>
  )
}

export function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="shell">
      <header className="shell__header">
        {/* The lockup is the way home — the convention everybody already knows,
            and on a phone it saves a trip to the bottom nav. The whole thing is
            one target rather than just the mark. */}
        <Link to="/" className="shell__home">
          <img className="shell__mark" src="/rs-logo.png" alt="" />
          <div className="shell__brand">
            SailNorse
            <span>Roland-Story</span>
          </div>
          <span className="visually-hidden">Go to home</span>
        </Link>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 'var(--s-3)', minWidth: 0 }}>
          <RoleBadge />
          <Bell />
          <AccountMenu />
        </div>
      </header>
      <PreviewBanner />
      <SideNav />
      <main className="shell__main">{children}</main>
    </div>
  )
}
